import type { CoverTextBox } from 'fym-shared/src/music24/coverDoc';
import { KIT_FONT_FACES } from 'fym-shared/src/music24/releaseThemes';
import { applyTextCase, trackedWidth, wrapLines } from './wrapText';

/** The lines and the size of a text box at one cover size, in pixels. */
export interface MeasuredCoverText {
  lines: string[];
  lineWidths: number[];
  fontPx: number;
  trackingPx: number;
  lineHeightPx: number;
  /** The wrap width of the box. */
  boxWidth: number;
  /** The height of all lines. */
  boxHeight: number;
  /** The CSS `font` value of the box at this size. */
  font: string;
}

/** The part of a 2D context that the measure needs (a test gives a fake). */
export interface TextMeasurer {
  font: string;
  measureText(text: string): { width: number };
}

/** The CSS `font` of a box at `fontPx`: the kit family with its generic fallback. Pure. */
export function coverFontCss(box: Pick<CoverTextBox, 'fontId' | 'weight' | 'italic'>, fontPx: number): string {
  const face = KIT_FONT_FACES[box.fontId];
  const style = box.italic || face.italic ? 'italic ' : '';
  return `${style}${box.weight} ${fontPx}px '${face.family}', ${face.fallback}`;
}

/**
 * Wraps and measures a text box for a cover of `side` pixels. The renderer and the selection box
 * of the stage both use it, so the handles sit on the drawn text. The letter spacing is a part of
 * each width. It sets `font` on the measurer.
 */
export function measureCoverText(box: CoverTextBox, side: number, measurer: TextMeasurer): MeasuredCoverText {
  const fontPx = box.sizeShare * side;
  const trackingPx = box.tracking * fontPx;
  const font = coverFontCss(box, fontPx);
  // The measure reads the font of the context.
  measurer.font = font;
  const width = (text: string): number => trackedWidth(text, trackingPx, (part) => measurer.measureText(part).width);
  const boxWidth = box.width * side;
  const lines = wrapLines(applyTextCase(box.text, box.textCase), boxWidth, width);
  const lineHeightPx = fontPx * box.lineHeight;
  return {
    lines,
    lineWidths: lines.map(width),
    fontPx,
    trackingPx,
    lineHeightPx,
    boxWidth,
    boxHeight: Math.max(1, lines.length) * lineHeightPx,
    font,
  };
}
